import React, { useEffect, useState } from "react";
import { useTable, useSortBy, usePagination } from "react-table";
import { PrevBtn, NextBtn, PaginationWrapper } from "../../Styles/PageStyles";
import Images from "../../../Images";
import tw from "tailwind-styled-components";
import { PaginationLimit } from "../../../Config";
import {
    capitalizeFirstLetter,
    truncateText
} from "../../../helpers";
import moment from "moment";
import { MdEdit, MdDelete } from "react-icons/md";
import Tooltip from "../../Tooltip";
import { FaKitchenSet } from "react-icons/fa6";
import { HiOutlineBuildingOffice2 } from "react-icons/hi2";
import { IoRestaurantOutline } from "react-icons/io5";
import { getDateTimeDisplay } from "../../../utilities/helpers";

const Table = ({
    apiData,
    selectedData,
    setSelectedData,
    setIsShowEditKitchen,
    setIsDeleteKitchen
}) => {
    const [data, setData] = useState([]);
    const [selectedRow, setSelectedRow] = useState(null);
    const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);
    const [currentPage, setCurrentPage] = useState(0);

    const editItem = (item) => {
        setSelectedData(item);
        setIsShowEditKitchen(true);
    };

    const deleteItem = (item) => {
        setSelectedData(item);
        setIsDeleteKitchen(true);
    };

    const toggleRow = (index) => {
        setSelectedRow(selectedRow === index ? null : index);
    };

    useEffect(() => {
        if (apiData && apiData.length > 0) {
            const tempData = apiData.map((item) => ({
                id: item.id,
                name: item.name,
                status: item.status,
                createdAt: item.createdAt,
                updatedAt: item.updatedAt,
                thisMonthAssignedPrice: item.thisMonthAssignedPrice,
                totalAssignedPrice: item.totalAssignedPrice,
                linkedProperties: item.linkedProperties || [],
                // For display
                kitchenName: getKitchenName(item),
                properties: getPropertiesDisplay(item.linkedProperties || []),
                statusBadge: getStatus(item),
                updatedAtDisplay: getDateTimeDisplay (item.updatedAt),
                actions: getActions(item),
            }));
            setData(tempData);
        } else {
            setData([]);
        }
    }, [apiData]);

    useEffect(() => {
        const handleResize = () => {
            setIsMobileView(window.innerWidth < 768);
        };

        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    const getKitchenName = (item) => {
        return (
            // <UserImageWrapper>
            //     {/* <IconWrapper>
            //         <IoRestaurantOutline className="text-primary-color" size={18} />
            //     </IconWrapper> */}
            //     <div className="flex flex-col min-w-0">
            //         <div className="flex items-center gap-2 group">
            //             <span className="font-medium text-gray-700 truncate">
            //                 {capitalizeFirstLetter(truncateText(item.name, 28))}
            //             </span>
            //         </div>
            //         {/* <span className="text-gray-400 text-xs">
            //             ID: #{item.id}
            //         </span> */}
            //     </div>
            // </UserImageWrapper>
            <div className="flex flex-col gap-0.5 pl-2">
                {/* <div className="flex items-center gap-2 group"> */}
                <span className="text-sm font-medium text-gray-800 truncate">
                    {capitalizeFirstLetter(truncateText(item.name, 28))}
                </span>
                {/* </div> */}
                {/* <span className="text-gray-400 text-xs">Transfer #{item.transferId}</span> */}
            </div>
        );
    };

    const getPropertiesDisplay = (properties) => {
        if (!properties || properties.length === 0) {
            return (
                <span className="text-gray-400 text-sm italic">
                    No properties linked
                </span>
            );
        }

        const displayCount = 2;
        const visibleProperties = properties.slice(0, displayCount);
        const remainingCount = properties.length - displayCount;

        return (
            <div className="flex flex-wrap items-center gap-1">
                {visibleProperties.map((prop, index) => (
                    <PropertyTag key={prop.id || index}>
                        <HiOutlineBuildingOffice2 className="w-3 h-3" />
                        {capitalizeFirstLetter(truncateText(prop.name, 20))}
                    </PropertyTag>
                ))}
                {remainingCount > 0 && (
                    <Tooltip
                        label={
                            <div className="p-1">
                                <p className="font-semibold text-xs mb-1">All Linked Properties:</p>
                                <ul className="space-y-1">
                                    {properties.slice(displayCount).map((prop, idx) => (
                                        <li key={prop.id || idx} className="text-xs flex items-center gap-1">
                                            <HiOutlineBuildingOffice2 className="w-3 h-3" />
                                            {capitalizeFirstLetter(prop.name)}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        }
                    >
                        <PropertyTag className="bg-gray-100 text-gray-600 cursor-help">
                            +{remainingCount} more
                        </PropertyTag>
                    </Tooltip>
                )}
            </div>
        );
    };

    const getStatus = (item) => {
        const isActive = item.status === 1;
        return (
            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${isActive
                ? 'bg-green-100 text-green-700'
                : 'bg-red-100 text-red-700'
                } w-fit`}>
                {isActive ? 'Active' : 'Inactive'}
            </span>
        );
    };

    const getActions = (item) => {
        return (
            <div className="flex items-center gap-3">
                <Tooltip label="Click to edit this kitchen">
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            editItem(item);
                        }}
                        className="text-gray-400 hover:text-primary-color transition"
                    >
                        <MdEdit size={18} />
                    </button>
                </Tooltip>
                <Tooltip label="Click to delete this kitchen">
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            deleteItem(item);
                        }}
                        className="p-2 rounded-full hover:bg-rose-100 transition cursor-pointer"
                    >
                        <MdDelete size={18} className="text-gray-400 hover:text-rose-500" />
                    </button>
                </Tooltip>
            </div>
        );
    };

    const columns = React.useMemo(() => {
        if (isMobileView) {
            return [
                {
                    Header: "Kitchen",
                    accessor: "kitchenName",
                },
                {
                    Header: "Properties",
                    accessor: "properties",
                },

                {
                    Header: "Actions",
                    accessor: "actions",
                },
            ];
        }
        return [
            {
                Header: "Kitchen Name",
                accessor: "kitchenName",
                sortType: (a, b) => a.original.name.localeCompare(b.original.name),
            },
            {
                Header: "Linked Properties",
                accessor: "properties",
                disableSortBy: true,
            },

            {
                Header: "Date",
                accessor: "updatedAtDisplay",
            },
            {
                Header: "Actions",
                accessor: "actions",
                disableSortBy: true,
            },
        ];
    }, [isMobileView]);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        pageOptions,
        page,
        state: { pageIndex },
        previousPage,
        nextPage,
        canPreviousPage,
        canNextPage,
    } = useTable(
        {
            data,
            columns,
            initialState: { pageIndex: currentPage, pageSize: PaginationLimit },
        },
        useSortBy,
        usePagination
    );

    useEffect(() => {
        setCurrentPage(pageIndex);
    }, [pageIndex]);

    return (
        <>
            <CustomTable {...getTableProps()}>
                <Thead>
                    {headerGroups.map((headerGroup) => (
                        <Tr {...headerGroup.getHeaderGroupProps()}>
                            {headerGroup.headers.map((column) => (
                                <Th {...column.getHeaderProps(column.getSortByToggleProps())}>
                                    {column.render("Header")}
                                    <span>
                                        {column.isSorted ? (
                                            <img
                                                src={Images.Arrow}
                                                alt="down arrow"
                                                className={`${column.isSortedDesc ? "-rotate-90" : "rotate-90"
                                                    } w-1.5 inline-block ml-1.5`}
                                            />
                                        ) : (
                                            ""
                                        )}
                                    </span>
                                </Th>
                            ))}
                        </Tr>
                    ))}
                </Thead>
                <Tbody {...getTableBodyProps()} hasdata={(data?.length > 0).toString()}>
                    {page.length > 0 && (
                        page.map((row, rowIndex) => {
                            prepareRow(row);
                            return (
                                <React.Fragment key={rowIndex}>
                                    <Tr {...row.getRowProps({ style: { height: "80px" } })} hasdata={(data?.length > 0).toString()}>
                                        {row.cells.map((cell, cellIndex) => {
                                            const isActionCell = cell.column.id === 'actions' ||
                                                (typeof cell.column.accessor === 'string' &&
                                                    cell.column.accessor === 'actions');
                                            return (
                                                <Td
                                                    {...cell.getCellProps()}
                                                    onClick={() => {
                                                        if (!isActionCell &&
                                                            (cellIndex === 0 ||
                                                                (cellIndex === 1 && window.innerWidth < 768))) {
                                                            toggleRow(rowIndex);
                                                        }
                                                    }}
                                                >
                                                    {cell.render("Cell")}
                                                </Td>
                                            );
                                        })}
                                    </Tr>
                                    {selectedRow === rowIndex && window.innerWidth < 768 && (
                                        <Tr>
                                            <Td colSpan={columns.length}>
                                                <ul className="flex flex-col gap-2 p-2">
                                                    <li>
                                                        <strong>Kitchen Name:</strong>{" "}
                                                        {data[rowIndex]?.name}
                                                    </li>

                                                    <li>
                                                        <strong>This Month Assigned Price:</strong>{" "}
                                                        ₹{data[rowIndex]?.thisMonthAssignedPrice || 0}
                                                    </li>
                                                    <li>
                                                        <strong>Total Assigned Price:</strong>{" "}
                                                        ₹{data[rowIndex]?.totalAssignedPrice || 0}
                                                    </li>
                                                    <li>
                                                        <strong>Linked Properties:</strong>
                                                        <ul className="ml-4 mt-1 space-y-1">
                                                            {data[rowIndex]?.linkedProperties?.map((prop, idx) => (
                                                                <li key={prop.id || idx} className="text-sm flex items-center gap-1">
                                                                    <HiOutlineBuildingOffice2 className="w-3 h-3" />
                                                                    {capitalizeFirstLetter(prop.name)}
                                                                </li>
                                                            ))}
                                                            {(!data[rowIndex]?.linkedProperties || data[rowIndex]?.linkedProperties.length === 0) && (
                                                                <li className="text-gray-400 text-sm italic">No properties linked</li>
                                                            )}
                                                        </ul>
                                                    </li>
                                                    <li>
                                                        <strong>Created At:</strong>{" "}
                                                        {moment(data[rowIndex]?.createdAt).format("DD, MMM YY hh:mm A")}
                                                    </li>
                                                    <li>
                                                        <strong>Last Updated:</strong>{" "}
                                                        {data[rowIndex]?.updatedAtDisplay}
                                                    </li>
                                                    <li>
                                                        <strong>Actions:</strong>{" "}
                                                        {data[rowIndex]?.actions}
                                                    </li>
                                                </ul>
                                            </Td>
                                        </Tr>
                                    )}
                                </React.Fragment>
                            );
                        })
                    )}
                </Tbody>
            </CustomTable>
            {data?.length > 0 && (
                <PaginationWrapper>
                    <div className="px-2">
                        Page{" "}
                        <em>
                            {pageIndex + 1} of {pageOptions.length}
                        </em>
                    </div>
                    <div className="flex gap-1">
                        <PrevBtn onClick={() => previousPage()} disabled={!canPreviousPage}>
                            <img src={Images.Arrow} alt="arrow" className="w-2 opacity-75" />
                        </PrevBtn>
                        <NextBtn onClick={() => nextPage()} disabled={!canNextPage}>
                            <img
                                src={Images.Arrow}
                                alt="arrow"
                                className="w-2 rotate-180 opacity-75"
                            />
                        </NextBtn>
                    </div>
                </PaginationWrapper>
            )}
        </>
    );
};

const UserImageWrapper = tw.div`flex items-center gap-2`;
const IconWrapper = tw.div`w-8 h-8 flex items-center justify-center rounded-full bg-primary-color/10`;
const PropertyTag = tw.span`
    inline-flex items-center gap-1 
    px-2 py-1 
    bg-primary-color/10 text-primary-color 
    rounded-full text-xs font-medium
    border border-primary-color/20
`;
const CustomTable = tw.table`w-full text-zinc-600`;
const Thead = tw.thead`border-b uppercase text-gray-600 bg-blue-50`;
const Tbody = tw.tbody`${(props) => (props.hasdata === "true" ? "border-b" : "")}`;
const Tr = tw.tr`${(props) =>
    props.hasdata === "true"
        ? "border-b"
        : ""} rounded-md overflow-hidden hover:bg-slate-50`;
const Th = tw.th`text-left text-sm md:p-3 p-0.5 font-normal text-gray-600`;
const Td = tw.td`p-1 text-sm md:p-2 text-left`;

export default Table;